/**
 * Technique explanations for How to play, paired with real example positions
 * found by the engine (examples.json, made by `npm run help-examples -w @sudoku/engine`).
 */
import examplesJson from './examples.json';
import type { Level } from '../game/types.ts';

export interface TechniqueExample {
  technique: string;
  values: number[];
  candidates: number[];
  step: {
    cells: number[];
    digits: number[];
    placements: { cell: number; digit: number }[];
    eliminations: { cell: number; digit: number }[];
    unit?: number;
    message: string;
  };
}

export interface TechniqueInfo {
  id: string;
  name: string;
  level: Level;
  /** One line for the cards. */
  short: string;
  /** Full explanation for the Techniques page. */
  long: string;
}

export const TECHNIQUE_INFO: TechniqueInfo[] = [
  {
    id: 'hidden-single',
    name: 'Hidden single',
    level: 'easy',
    short: 'A digit fits only one cell in a row, column or box.',
    long: 'Look at one row, column or box and one digit. If every cell but one is ruled out for that digit, because the digit is already in that cell’s row, column or box, the digit must go in the one cell left.',
  },
  {
    id: 'naked-single',
    name: 'Naked single',
    level: 'easy',
    short: 'A cell can take only one digit.',
    long: 'Look at one cell. If eight of the nine digits already appear in its row, column or box, the ninth is the only digit that can go there.',
  },
  {
    id: 'pointing',
    name: 'Pointing',
    level: 'medium',
    short: 'Inside a box, a digit is confined to one line, so it leaves that line elsewhere.',
    long: 'Inside one box, the cells where a digit can go all lie in the same row (or the same column). The digit must sit on that line within this box, so it cannot appear anywhere else on that line. Remove it from the line’s cells outside the box.',
  },
  {
    id: 'box-line-reduction',
    name: 'Box/line reduction',
    level: 'medium',
    short: 'In a row or column, a digit is confined to one box, so it leaves the rest of that box.',
    long: 'In one row (or column), the cells where a digit can go all lie inside the same box. The digit must be on this line within that box, so remove it from the box’s other cells.',
  },
  {
    id: 'naked-pair',
    name: 'Naked pair',
    level: 'hard',
    short: 'Two cells share the same two possibilities, so those digits leave the rest of the unit.',
    long: 'Two cells in the same row, column or box can each take only the same two digits. Between them they must use both digits, so no other cell in that row, column or box can have either one. Remove them from the others.',
  },
  {
    id: 'hidden-pair',
    name: 'Hidden pair',
    level: 'hard',
    short: 'Two digits fit only the same two cells, so those cells hold nothing else.',
    long: 'In a row, column or box, two digits can each go only in the same two cells. Those two cells must hold those two digits, so every other possibility in them can be removed.',
  },
  {
    id: 'naked-triple',
    name: 'Naked triple',
    level: 'extra-hard',
    short: 'Three cells hold only three digits between them.',
    long: 'Like a naked pair, with three cells. Three cells in one unit have only three digits among them (each cell need not have all three). Those digits are used up by the three cells, so remove them from the rest of the unit.',
  },
  {
    id: 'hidden-triple',
    name: 'Hidden triple',
    level: 'extra-hard',
    short: 'Three digits fit only the same three cells.',
    long: 'Like a hidden pair, with three digits. In one unit, three digits can only go in the same three cells, so those cells hold those digits and nothing else.',
  },
  {
    id: 'x-wing',
    name: 'X-Wing',
    level: 'extra-hard',
    short: 'A digit forms a rectangle across two rows and two columns.',
    long: 'In two rows, a digit can only go in the same two columns. It must take opposite corners of that rectangle, one in each row and one in each column. So it cannot appear anywhere else in those two columns. The same works with rows and columns swapped.',
  },
  {
    id: 'y-wing',
    name: 'Y-Wing',
    level: 'extra-hard',
    short: 'A pivot and two wings force a digit into one wing or the other.',
    long: 'A pivot cell with two possibilities, say X and Y, sees two “wing” cells: one with X and Z, one with Y and Z. Whatever the pivot is, one wing must be Z. So any cell that sees both wings cannot be Z.',
  },
  {
    id: 'naked-quad',
    name: 'Naked quad',
    level: 'extra-hard',
    short: 'Four cells hold only four digits between them.',
    long: 'The four-cell version of a naked pair. Four cells in one unit have only four digits among them, so those digits can be removed from the rest of the unit.',
  },
  {
    id: 'hidden-quad',
    name: 'Hidden quad',
    level: 'extra-hard',
    short: 'Four digits fit only the same four cells.',
    long: 'The four-digit version of a hidden pair. Four digits can only go in the same four cells of a unit, so those cells hold nothing else.',
  },
  {
    id: 'swordfish',
    name: 'Swordfish',
    level: 'experts-only',
    short: 'An X-Wing across three rows and three columns.',
    long: 'In three rows, a digit can only go in the same three columns (two or three places per row). Those three rows use up the digit in those three columns, so remove it from every other cell of those columns. Also works with rows and columns swapped.',
  },
  {
    id: 'xyz-wing',
    name: 'XYZ-Wing',
    level: 'experts-only',
    short: 'Like a Y-Wing, but the pivot can also be the shared digit.',
    long: 'A pivot with three possibilities X, Y and Z sees two wings, one with X and Z, one with Y and Z. One of the three cells must be Z, so any cell that sees all three cannot be Z.',
  },
  {
    id: 'simple-coloring',
    name: 'Simple coloring',
    level: 'experts-only',
    short: 'Follow a digit’s two-place links in two colors; one color is true.',
    long: 'Pick a digit. Wherever it has exactly two places in a unit, one of the two must be it. Chain these pairs together, coloring alternately in two colors: one color is all true, the other all false. If two cells of the same color see each other, that color is false. Any cell that sees both colors cannot hold the digit.',
  },
  {
    id: 'xy-chain',
    name: 'XY-Chain',
    level: 'experts-only',
    short: 'A chain of two-possibility cells forces a digit at one end or the other.',
    long: 'Link cells that each have two possibilities, where each cell sees the next and shares a digit with it. If one end is not X, the chain forces the other end to be X. So one of the two ends is X, and any cell that sees both ends cannot be X.',
  },
];

export const EXAMPLES: Record<string, TechniqueExample> = Object.fromEntries(
  (examplesJson as TechniqueExample[]).map((e) => [e.technique, e]),
);

const unitCells = (unit: number): number[] => {
  if (unit < 9) return Array.from({ length: 9 }, (_, i) => unit * 9 + i);
  if (unit < 18) return Array.from({ length: 9 }, (_, i) => i * 9 + (unit - 9));
  const b = unit - 18;
  return Array.from({ length: 9 }, (_, i) => (3 * Math.floor(b / 3) + Math.floor(i / 3)) * 9 + 3 * (b % 3) + (i % 3));
};

/** The rows and columns worth showing for an example: whole box bands around what matters. */
export function exampleView(ex: TechniqueExample) {
  const area = ex.step.unit !== undefined ? unitCells(ex.step.unit) : [];
  const involved = [...ex.step.cells, ...ex.step.eliminations.map((e) => e.cell), ...ex.step.placements.map((p) => p.cell), ...area];
  const rows = involved.map((c) => Math.floor(c / 9));
  const cols = involved.map((c) => c % 9);
  const band = (xs: number[]): [number, number] => [Math.floor(Math.min(...xs) / 3) * 3, Math.floor(Math.max(...xs) / 3) * 3 + 2];
  return { area, rows: band(rows), cols: band(cols) };
}
